// ─────────────────────────────────────────────────────────────────────────
// Rendiciones — gastos con tarjeta de la empresa (26-ago-2026, Fase C).
//
// Rinden: Maxi, José y Nicolás. Pensado para el CELULAR en terreno:
// monto grande → categoría de un toque → foto de la boleta → REGISTRAR.
// Los gastos de Maxi (admin) nacen aprobados; los demás quedan "pendiente"
// y Maxi los aprueba/rechaza desde esta misma pantalla (ve todas).
// ─────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useRef, useState } from "react";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import { formatPrice } from "../../utils";

const CATEGORIAS = [
  ["combustible", "⛽ Bencina"],
  ["materiales",  "🔩 Materiales"],
  ["ferreteria",  "🔧 Ferretería"],
  ["transporte",  "🚚 Transporte"],
  ["estacionamiento", "🅿️ Estacionamiento"],
  ["alimentacion","🍽 Comida"],
  ["peajes",      "🛣 Peajes"],
  ["servicios",   "🧾 Servicios"],
  ["otros",       "📦 Otros"],
];
const CAT_LABEL = Object.fromEntries(CATEGORIAS);

const ESTADO = {
  pendiente: { label: "Pendiente", color: "#f39c12", bg: "#2d2000" },
  aprobada:  { label: "Aprobada",  color: "#2ecc71", bg: "#0d2a15" },
  rechazada: { label: "Rechazada", color: "#e74c3c", bg: "#2d1515" },
};

const fmt = n => `$${formatPrice(Math.round(n || 0))}`;
const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "10px 12px", fontSize: 16, color: "#ddd", width: "100%", boxSizing: "border-box" },
  // Tabla desktop (pulido 27-ago)
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: { padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525", verticalAlign: "top" },
};

// Medio de pago legible (desktop) + estado de devolución y factura enlazada
const medioTxt = r => {
  let s = r.medioPago === "tarjeta_empresa"
    ? `💳 ${r.bancoTarjeta === "chile" ? "Bco. Chile" : r.bancoTarjeta === "santander" ? "Santander" : "tarjeta"}`
    : r.medioPago === "caja" ? "💵 caja" : "🧍 reembolso";
  if (r.medioPago === "reembolso" && r.devolucion?.pagada) s += " · devuelto ✓";
  if (r.facturaVinculada?.folio) s += ` · 🧾 F${r.facturaVinculada.folio}`;
  return s;
};

const MEDIOS = [
  ["tarjeta_santander", "💳 Tarjeta Santander"],
  ["tarjeta_chile", "💳 Tarjeta Bco. Chile"],
  ["reembolso", "🧍 Pagué yo (me lo devuelven)"],
  ["caja", "💵 Caja chica"],
];

const mesActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Rendiciones = () => {
  useTitle("Gastos");
  const auth = useContext(AuthContext);
  const roles = auth?.user?.roles || [];
  const esAdmin = roles.includes("admin");
  const puedeVincularFactura = roles.some(r => ["admin", "accountant"].includes(r));
  // Pulido desktop 27-ago: móvil intacto (form grande + tarjetas); desktop =
  // form compacto en una fila + lista como tabla.
  const isMobile = useIsMobile();

  const [monto, setMonto] = useState("");
  const [categoria, setCategoria] = useState("combustible");
  const [descripcion, setDescripcion] = useState("");
  const [conFactura, setConFactura] = useState(false);
  // 27-ago (aclaración Maxi): la regla es TARJETA DE EMPRESA — el registro es
  // para que el contador sepa qué fue cada cargo; el reembolso es la excepción.
  const [medioPago, setMedioPago] = useState("tarjeta_santander"); // tarjeta_santander|tarjeta_chile|reembolso|caja
  const [foto, setFoto] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const fotoRef = useRef(null);

  const [mes, setMes] = useState(mesActual());
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);

  // Filtros de la lista (28-ago): chip de categoría (toggle) + "por aprobar"
  const [filtroCat, setFiltroCat] = useState(null);       // null = todas
  const [soloPendientes, setSoloPendientes] = useState(false);

  // Mini-modales (28-ago): reemplazan los window.prompt de rechazo/devolución
  const [rechazoTarget, setRechazoTarget] = useState(null);
  const [rechazoMotivo, setRechazoMotivo] = useState("");
  const [devolTarget, setDevolTarget] = useState(null);
  const [devolMedio, setDevolMedio] = useState("transferencia");
  const [vincularTarget, setVincularTarget] = useState(null);
  const [facturasSii, setFacturasSii] = useState([]);
  const [facturaId, setFacturaId] = useState("");
  const [cargandoFacturas, setCargandoFacturas] = useState(false);

  const cargar = async (m = mes) => {
    setCargando(true);
    try {
      const r = await backend.get("rendicion", { params: { mes: m } });
      setRows(r.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  };
  useEffect(() => { cargar(mes); /* eslint-disable-next-line */ }, [mes]);

  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); setTimeout(() => setAviso(null), 3500); };

  const registrar = async () => {
    const n = Math.round(Number(monto) || 0);
    if (n <= 0) { avisar("error", "Ingresa el monto"); return; }
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append("monto", String(n));
      fd.append("categoria", categoria);
      if (descripcion.trim()) fd.append("descripcion", descripcion.trim());
      fd.append("tieneFactura", String(conFactura));
      fd.append("medioPago", medioPago.startsWith("tarjeta_") ? "tarjeta_empresa" : medioPago);
      if (medioPago.startsWith("tarjeta_")) fd.append("bancoTarjeta", medioPago.replace("tarjeta_", ""));
      if (foto) fd.append("foto", foto);
      await backend.post("rendicion", fd, { headers: { "Content-Type": "multipart/form-data" } });
      try { navigator.vibrate?.(60); } catch { /* sin vibración */ }
      avisar("ok", `${fmt(n)} registrado${esAdmin ? "" : " — queda pendiente de aprobación"}`);
      setMonto(""); setDescripcion(""); setFoto(null); setConFactura(false);
      if (fotoRef.current) fotoRef.current.value = "";
      cargar();
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
    setGuardando(false);
  };

  const cambiarEstado = async (r, estado, motivo = "") => {
    try {
      await backend.patch(`rendicion/${r._id}/estado`, { estado, motivo });
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
  };

  // Rechazo con motivo (28-ago): mini-modal en vez de window.prompt.
  const abrirRechazo = r => { setRechazoTarget(r); setRechazoMotivo(""); };
  const confirmarRechazo = async () => {
    if (!rechazoTarget) return;
    await cambiarEstado(rechazoTarget, "rechazada", rechazoMotivo.trim());
    setRechazoTarget(null);
    setRechazoMotivo("");
  };

  // Devolución de un reembolso (27-ago): la empresa le transfirió a la persona.
  // 28-ago: mini-modal con el medio, en vez de window.prompt.
  const abrirDevolucion = r => { setDevolTarget(r); setDevolMedio("transferencia"); };
  const confirmarDevolucion = async () => {
    if (!devolTarget) return;
    try {
      const fd = new FormData();
      fd.append("medio", devolMedio.trim() || "transferencia");
      await backend.patch(`rendicion/${devolTarget._id}/devolucion`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      avisar("ok", "Devolución registrada");
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
    setDevolTarget(null);
    setDevolMedio("transferencia");
  };

  const abrirVincularFactura = async (rendicion) => {
    setVincularTarget(rendicion);
    setFacturaId(rendicion.facturaVinculada?._id || "");
    setCargandoFacturas(true);
    try {
      const { data } = await backend.get("sii/facturas", { params: { periodo: mes, tipo: "compra" } });
      setFacturasSii(data || []);
    } catch (e) {
      avisar("error", e?.response?.data?.error || "No se pudieron cargar las facturas SII");
      setFacturasSii([]);
    } finally {
      setCargandoFacturas(false);
    }
  };

  const confirmarVinculoFactura = async () => {
    if (!vincularTarget) return;
    try {
      const { data } = await backend.patch(`rendicion/${vincularTarget._id}/vincular`, {
        facturaId: facturaId || null,
        factura: facturaId || null,
      });
      setRows(prev => prev.map(r => r._id === vincularTarget._id ? { ...r, ...data } : r));
      avisar("ok", facturaId ? "Factura SII vinculada" : "Vínculo con factura eliminado");
      setVincularTarget(null);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
  };

  const totalMes = rows.filter(r => r.estado !== "rechazada").reduce((s, r) => s + (r.monto || 0), 0);
  // Reembolsos APROBADOS aún sin devolver (cualquier mes cargado): plata que se le debe a la gente.
  const porDevolver = rows.filter(r => r.medioPago === "reembolso" && r.estado === "aprobada" && !r.devolucion?.pagada);
  const totalPorDevolver = porDevolver.reduce((s, r) => s + (r.monto || 0), 0);
  const pendientes = rows.filter(r => r.estado === "pendiente");
  const porCategoria = {};
  rows.filter(r => r.estado !== "rechazada").forEach(r => {
    porCategoria[r.categoria] = (porCategoria[r.categoria] || 0) + (r.monto || 0);
  });

  // Lista filtrada (28-ago): los totales de arriba siguen siendo del mes completo.
  const visibles = rows.filter(r =>
    (!filtroCat || r.categoria === filtroCat) &&
    (!soloPendientes || r.estado === "pendiente")
  );
  const hayFiltro = !!filtroCat || soloPendientes;

  return (
    <div style={{ padding: "12px 12px 24px", background: "#1a1a1a", minHeight: "100%", color: "#ddd", maxWidth: isMobile ? 760 : 1400 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>💳 Gastos y rendiciones</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
      </div>

      {/* Reembolsos pendientes de devolver (27-ago): plata que se le debe a la gente */}
      {esAdmin && porDevolver.length > 0 && (
        <div style={{ ...S.card, marginBottom: 12, border: "1px solid #4a3500", background: "#221a00" }}>
          <div style={{ ...S.label, color: "#f39c12", marginBottom: 6 }}>💸 Por devolver ({porDevolver.length}) — {fmt(totalPorDevolver)}</div>
          {porDevolver.map(r => (
            <div key={r._id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #332800", flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 140, fontSize: 12, color: "#ddd" }}>
                {r.porEmail?.split("@")[0] || "—"} · {r.descripcion || CAT_LABEL[r.categoria] || r.categoria}
                <span style={{ color: "#8a8a8a", fontSize: 11 }}> · {new Date(r.fecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}</span>
              </span>
              <span style={{ fontWeight: 800, color: "#f39c12", ...num }}>{fmt(r.monto)}</span>
              <button onClick={() => abrirDevolucion(r)} className="no-touch-min t-02"
                style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 6, padding: "7px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", minHeight: 36 }}>
                💸 Marcar devuelto
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Registro rápido ── (móvil: form grande táctil; desktop: fila compacta) */}
      {isMobile ? (
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ ...S.label, marginBottom: 6 }}>Nuevo gasto de tarjeta</div>
        <input
          type="number" inputMode="numeric" min={1}
          placeholder="$ Monto"
          value={monto}
          onChange={e => setMonto(e.target.value)}
          style={{ ...S.input, fontSize: 22, fontWeight: 700, minHeight: 52, textAlign: "center", ...num }}
        />
        <div className="chips-scroll" style={{ display: "flex", gap: 6, margin: "10px 0", flexWrap: "wrap" }}>
          {CATEGORIAS.map(([k, l]) => (
            <button key={k} type="button" className="chip-touch no-touch-min t-02"
              onClick={() => setCategoria(k)}
              style={{
                background: categoria === k ? "#3a1114" : "#1e1e1e",
                color: categoria === k ? "#ff8a8f" : "#999",
                border: `1px solid ${categoria === k ? "#e30613" : "#333"}`,
                borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}>
              {l}
            </button>
          ))}
        </div>
        <input
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Detalle (opcional): dónde, para qué"
          style={{ ...S.input, marginBottom: 10 }}
        />
        {/* ¿Con qué se pagó? — la tarjeta de empresa es la regla */}
        <div className="chips-scroll" style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {[
            ["tarjeta_santander", "💳 Tarjeta Santander"],
            ["tarjeta_chile", "💳 Tarjeta Bco. Chile"],
            ["reembolso", "🧍 Pagué yo (me lo devuelven)"],
            ["caja", "💵 Caja chica"],
          ].map(([k, l]) => (
            <button key={k} type="button" className="chip-touch no-touch-min t-02"
              onClick={() => setMedioPago(k)}
              style={{
                background: medioPago === k ? "#0d2035" : "#1e1e1e",
                color: medioPago === k ? "#3498db" : "#999",
                border: `1px solid ${medioPago === k ? "#3498db" : "#333"}`,
                borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}>
              {l}
            </button>
          ))}
        </div>
        {/* Hallazgo contador 26-ago: con FACTURA el IVA se recupera como crédito */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer", fontSize: 13, color: conFactura ? "#2ecc71" : "#b8b8b8" }}>
          <input type="checkbox" checked={conFactura} onChange={e => setConFactura(e.target.checked)}
            style={{ width: 20, height: 20, accentColor: "#2ecc71" }} />
          Fue con FACTURA a nombre de la empresa (el gasto y el IVA llegan solos del SII; esto registra el pago) — con boleta, dejar sin marcar
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={fotoRef}
            type="file" accept="image/*" capture="environment"
            onChange={e => setFoto(e.target.files?.[0] || null)}
            style={{ fontSize: 12, color: "#aaa", flex: 1, minWidth: 160 }}
          />
          <button
            onClick={registrar} disabled={guardando}
            className="t-02 tap-card"
            style={{
              minHeight: 48, borderRadius: 10, border: "none",
              background: guardando ? "#333" : "#e30613", color: "#fff",
              fontSize: 15, fontWeight: 700, cursor: "pointer", padding: "0 26px",
            }}>
            {guardando ? "Guardando…" : "Registrar"}
          </button>
        </div>
        <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 6 }}>
          📷 Saca foto a la boleta — queda amarrada al gasto para el contador.
        </div>
      </div>
      ) : (
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ ...S.label, marginBottom: 8 }}>Nuevo gasto de tarjeta</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number" inputMode="numeric" min={1} placeholder="$ Monto"
            value={monto} onChange={e => setMonto(e.target.value)}
            style={{ ...S.input, width: 130, fontSize: 14, fontWeight: 700, textAlign: "right", ...num }}
          />
          <select value={categoria} onChange={e => setCategoria(e.target.value)}
            style={{ ...S.input, width: 180, fontSize: 13 }}>
            {CATEGORIAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={medioPago} onChange={e => setMedioPago(e.target.value)}
            style={{ ...S.input, width: 210, fontSize: 13 }}>
            {MEDIOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input
            value={descripcion} onChange={e => setDescripcion(e.target.value)}
            placeholder="Detalle (opcional): dónde, para qué"
            style={{ ...S.input, flex: "1 1 220px", minWidth: 180, fontSize: 13 }}
          />
          <input
            ref={fotoRef}
            type="file" accept="image/*"
            onChange={e => setFoto(e.target.files?.[0] || null)}
            style={{ fontSize: 12, color: "#aaa", width: 220, flexShrink: 0 }}
          />
          <button
            onClick={registrar} disabled={guardando} className="t-02"
            style={{ minHeight: 40, borderRadius: 8, border: "none", background: guardando ? "#333" : "#e30613", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 22px" }}>
            {guardando ? "Guardando…" : "Registrar"}
          </button>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer", fontSize: 12, color: conFactura ? "#2ecc71" : "#b8b8b8" }}>
          <input type="checkbox" checked={conFactura} onChange={e => setConFactura(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "#2ecc71" }} />
          Fue con FACTURA a nombre de la empresa (el gasto y el IVA llegan solos del SII; esto registra el pago) — con boleta, dejar sin marcar
        </label>
      </div>
      )}

      {/* ── Resumen del mes ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <input type="month" value={mes} onChange={e => setMes(e.target.value)}
          style={{ ...S.input, width: 160, fontSize: 13 }} />
        <div style={{ ...S.card, padding: "8px 14px", flex: 1, minWidth: 150, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={S.label}>Total mes</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: "#e74c3c", ...num }}>{fmt(totalMes)}</span>
        </div>
        {esAdmin && (pendientes.length > 0 || soloPendientes) && (
          <div
            onClick={() => setSoloPendientes(p => !p)}
            title={soloPendientes ? "Quitar el filtro de pendientes" : "Filtrar la lista: solo gastos por aprobar"}
            className="t-02"
            style={{
              ...S.card, padding: "8px 14px", display: "flex", gap: 8, alignItems: "center",
              cursor: "pointer", userSelect: "none",
              border: `1px solid ${soloPendientes ? "#f39c12" : "#4a3500"}`,
              background: soloPendientes ? "#2a2008" : "#232323",
            }}>
            <span style={{ fontSize: 12, color: "#f39c12", fontWeight: 700 }}>
              {pendientes.length} por aprobar{soloPendientes ? " ✕" : ""}
            </span>
          </div>
        )}
      </div>
      {Object.keys(porCategoria).length > 0 && (
        <div className="chips-scroll" style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
          {Object.entries(porCategoria).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
            const activo = filtroCat === k;
            return (
              <button key={k} type="button" className="chip-touch no-touch-min t-02"
                onClick={() => setFiltroCat(prev => prev === k ? null : k)}
                title={activo ? "Quitar el filtro de categoría" : `Filtrar la lista: ${CAT_LABEL[k] || k}`}
                style={{
                  fontSize: 11, color: activo ? "#ff8a8f" : "#b8b8b8",
                  background: activo ? "#3a1114" : "#232323",
                  border: `1px solid ${activo ? "#e30613" : "#2e2e2e"}`,
                  borderRadius: 8, padding: "4px 10px", whiteSpace: "nowrap",
                  cursor: "pointer", fontWeight: activo ? 700 : 400, ...num,
                }}>
                {CAT_LABEL[k] || k}: {fmt(v)}{activo ? " ✕" : ""}
              </button>
            );
          })}
          {hayFiltro && (
            <button type="button" className="t-02"
              onClick={() => { setFiltroCat(null); setSoloPendientes(false); }}
              style={{ background: "transparent", border: "1px solid #444", color: "#aaa", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
              Limpiar ({visibles.length} de {rows.length})
            </button>
          )}
        </div>
      )}

      {/* ── Lista del mes ── */}
      {cargando ? (
        <div style={{ color: "#8a8a8a", fontSize: 12, padding: 16 }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>Sin gastos este mes.</div>
      ) : visibles.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          Ningún gasto coincide con el filtro.
        </div>
      ) : !isMobile ? (
        /* ── Desktop: tabla del mes ── */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "80px" }} />  {/* Fecha */}
              <col style={{ width: "150px" }} /> {/* Categoría */}
              <col style={{ width: "100px" }} /> {/* Quién */}
              <col style={{ width: "120px" }} /> {/* Medio */}
              <col />                            {/* Detalle / IA */}
              <col style={{ width: "100px" }} /> {/* Monto */}
              <col style={{ width: "95px" }} />  {/* Estado */}
              <col style={{ width: "185px" }} /> {/* Acciones */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Fecha</th>
                <th style={S.th}>Categoría</th>
                <th style={S.th}>Quién</th>
                <th style={S.th}>Medio</th>
                <th style={S.th}>Detalle · análisis IA</th>
                <th style={{ ...S.th, textAlign: "right" }}>Monto</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((r, i) => {
                const e = ESTADO[r.estado] || ESTADO.pendiente;
                return (
                  <tr key={r._id} className="bini-row-hover"
                    style={{ background: i % 2 === 0 ? "#232323" : "#212121" }}>
                    <td style={{ ...S.td, whiteSpace: "nowrap", ...num }}>
                      {new Date(r.fecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}
                    </td>
                    <td style={{ ...S.td, color: "#fff", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {CAT_LABEL[r.categoria] || r.categoria}
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.porEmail?.split("@")[0] || "—"}</td>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>{medioTxt(r)}</td>
                    <td style={S.td}>
                      {r.descripcion && <div style={{ color: "#ddd", fontSize: 12 }}>{r.descripcion}</div>}
                      {r.analisisBoleta?.analizadoEn && !r.analisisBoleta.error && r.analisisBoleta.comercio && (
                        <div style={{ fontSize: 11, color: "#8fb8d8" }}>
                          🤖 {r.analisisBoleta.comercio}
                          {r.analisisBoleta.folio ? ` · ${r.analisisBoleta.tipoDocumento || "doc"} ${r.analisisBoleta.folio}` : ""}
                          {r.analisisBoleta.fecha ? ` · ${r.analisisBoleta.fecha}` : ""}
                          {r.analisisBoleta.detalleItems ? ` · ${r.analisisBoleta.detalleItems}` : ""}
                          {r.analisisBoleta.categoriaCorregida ? " · categoría corregida por IA" : ""}
                        </div>
                      )}
                      {r.analisisBoleta?.discrepanciaMonto && (
                        <div style={{ fontSize: 11, color: "#f39c12", fontWeight: 700 }}>
                          ⚠ La boleta dice {fmt(r.analisisBoleta.montoTotal)} y se digitó {fmt(r.monto)} — revisar
                        </div>
                      )}
                      {r.estado === "rechazada" && r.motivoRechazo && (
                        <div style={{ fontSize: 11, color: "#e74c3c" }}>Rechazo: {r.motivoRechazo}</div>
                      )}
                      {!r.descripcion && !r.analisisBoleta?.comercio && !r.analisisBoleta?.discrepanciaMonto && !(r.estado === "rechazada" && r.motivoRechazo) && "—"}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", color: "#fff", fontWeight: 800, whiteSpace: "nowrap", ...num }}>{fmt(r.monto)}</td>
                    <td style={S.td}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: e.color, background: e.bg, border: `1px solid ${e.color}33`, whiteSpace: "nowrap" }}>
                        {e.label}
                      </span>
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {r.fotoUrl && (
                          <a href={r.fotoUrl} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: "#3498db", textDecoration: "none" }}>
                            📷 Boleta
                          </a>
                        )}
                        {puedeVincularFactura && r.tieneFactura && (
                          <button onClick={() => abrirVincularFactura(r)} className="t-02"
                            style={{ background: "#0d1a2d", color: "#3498db", border: "1px solid #173b61", borderRadius: 6, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>
                            {r.facturaVinculada ? `🧾 F${r.facturaVinculada.folio}` : "Vincular factura"}
                          </button>
                        )}
                        {esAdmin && r.estado === "pendiente" && (
                          <>
                            <button onClick={() => cambiarEstado(r, "aprobada")} className="t-02"
                              style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              ✓ Aprobar
                            </button>
                            <button onClick={() => abrirRechazo(r)} className="t-02"
                              style={{ background: "#2d1515", color: "#e74c3c", border: "1px solid #4a2020", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              × Rechazar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visibles.map(r => {
            const e = ESTADO[r.estado] || ESTADO.pendiente;
            return (
              <div key={r._id} style={{ ...S.card, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                      {CAT_LABEL[r.categoria] || r.categoria}
                      <span style={{ fontWeight: 400, color: "#8a8a8a", fontSize: 11 }}>
                        {" "}· {new Date(r.fecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short" })} · {r.porEmail?.split("@")[0] || "—"}
                        {" "}· {medioTxt(r)}
                      </span>
                    </div>
                    {r.descripcion && <div style={{ fontSize: 11, color: "#b8b8b8", marginTop: 2 }}>{r.descripcion}</div>}
                    {/* Análisis IA de la boleta (27-ago): lo que la foto DICE */}
                    {r.analisisBoleta?.analizadoEn && !r.analisisBoleta.error && r.analisisBoleta.comercio && (
                      <div style={{ fontSize: 10.5, color: "#8fb8d8", marginTop: 3 }}>
                        🤖 {r.analisisBoleta.comercio}
                        {r.analisisBoleta.folio ? ` · ${r.analisisBoleta.tipoDocumento || "doc"} ${r.analisisBoleta.folio}` : ""}
                        {r.analisisBoleta.fecha ? ` · ${r.analisisBoleta.fecha}` : ""}
                        {r.analisisBoleta.detalleItems ? ` · ${r.analisisBoleta.detalleItems}` : ""}
                        {r.analisisBoleta.categoriaCorregida ? " · categoría corregida por IA" : ""}
                      </div>
                    )}
                    {r.analisisBoleta?.discrepanciaMonto && (
                      <div style={{ fontSize: 11, color: "#f39c12", marginTop: 2, fontWeight: 700 }}>
                        ⚠ La boleta dice {fmt(r.analisisBoleta.montoTotal)} y se digitó {fmt(r.monto)} — revisar
                      </div>
                    )}
                    {r.estado === "rechazada" && r.motivoRechazo && (
                      <div style={{ fontSize: 11, color: "#e74c3c", marginTop: 2 }}>Rechazo: {r.motivoRechazo}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", ...num }}>{fmt(r.monto)}</div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: e.color, background: e.bg, border: `1px solid ${e.color}33` }}>
                      {e.label}
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                  {r.fotoUrl && (
                    <a href={r.fotoUrl} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: "#3498db", textDecoration: "none" }}>
                      📷 Ver boleta
                    </a>
                  )}
                  {puedeVincularFactura && r.tieneFactura && (
                    <button onClick={() => abrirVincularFactura(r)} className="no-touch-min t-02"
                      style={{ background: "#0d1a2d", color: "#3498db", border: "1px solid #173b61", borderRadius: 6, padding: "8px 12px", fontSize: 11, minHeight: 38 }}>
                      {r.facturaVinculada ? `🧾 F${r.facturaVinculada.folio}` : "Vincular factura SII"}
                    </button>
                  )}
                  {esAdmin && r.estado === "pendiente" && (
                    <>
                      <button onClick={() => cambiarEstado(r, "aprobada")}
                        className="no-touch-min t-02"
                        style={{ background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020", borderRadius: 6, padding: "8px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", minHeight: 38 }}>
                        ✓ Aprobar
                      </button>
                      <button onClick={() => abrirRechazo(r)}
                        className="no-touch-min t-02"
                        style={{ background: "#2d1515", color: "#e74c3c", border: "1px solid #4a2020", borderRadius: 6, padding: "8px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", minHeight: 38 }}>
                        × Rechazar
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mini-modal: rechazar gasto con motivo (28-ago, reemplaza window.prompt) */}
      <ConfirmModal
        open={!!rechazoTarget}
        title={`Rechazar gasto de ${fmt(rechazoTarget?.monto)}`}
        description={`${rechazoTarget ? (CAT_LABEL[rechazoTarget.categoria] || rechazoTarget.categoria) : ""} · ${rechazoTarget?.porEmail?.split("@")[0] || "—"}${rechazoTarget?.descripcion ? ` · ${rechazoTarget.descripcion}` : ""}`}
        confirmLabel="× Rechazar gasto"
        confirmVariant="danger"
        onConfirm={confirmarRechazo}
        onCancel={() => { setRechazoTarget(null); setRechazoMotivo(""); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>Motivo del rechazo (se le muestra a quien rindió)</label>
          <textarea
            className="bini-input"
            rows={2}
            value={rechazoMotivo}
            onChange={e => setRechazoMotivo(e.target.value)}
            placeholder="Ej: La boleta no corresponde a un gasto de la empresa."
            style={{ resize: "vertical", minHeight: 44 }}
            autoFocus
          />
        </div>
      </ConfirmModal>

      {/* Mini-modal: registrar devolución de un reembolso (28-ago) */}
      <ConfirmModal
        open={!!devolTarget}
        title={`Devolución de ${fmt(devolTarget?.monto)}`}
        description={`A ${devolTarget?.porEmail?.split("@")[0] || "la persona"} · ${devolTarget ? (CAT_LABEL[devolTarget.categoria] || devolTarget.categoria) : ""}${devolTarget?.descripcion ? ` · ${devolTarget.descripcion}` : ""}`}
        confirmLabel="💸 Registrar devolución"
        confirmVariant="success"
        onConfirm={confirmarDevolucion}
        onCancel={() => { setDevolTarget(null); setDevolMedio("transferencia"); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>Medio de la devolución</label>
          <input
            className="bini-input"
            value={devolMedio}
            onChange={e => setDevolMedio(e.target.value)}
            placeholder="transferencia, efectivo…"
            autoFocus
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={!!vincularTarget}
        title={`Vincular factura SII · ${fmt(vincularTarget?.monto)}`}
        description={`${vincularTarget ? (CAT_LABEL[vincularTarget.categoria] || vincularTarget.categoria) : ""}${vincularTarget?.descripcion ? ` · ${vincularTarget.descripcion}` : ""}`}
        confirmLabel="Guardar vínculo"
        confirmVariant="success"
        onConfirm={confirmarVinculoFactura}
        onCancel={() => { setVincularTarget(null); setFacturaId(""); }}
      >
        <div className="bini-field" style={{ marginBottom: 12 }}>
          <label>Factura de compra del SII</label>
          <select className="bini-input" value={facturaId} onChange={e => setFacturaId(e.target.value)} disabled={cargandoFacturas}>
            <option value="">{cargandoFacturas ? "Cargando facturas…" : "Sin vínculo"}</option>
            {facturasSii.map(f => (
              <option key={f._id} value={f._id}>
                F{f.folio} · {f.razonEmisor || f.rutEmisor || "Proveedor"} · {fmt(f.montoTotal ?? f.monto)}
              </option>
            ))}
          </select>
          {!cargandoFacturas && facturasSii.length === 0 && (
            <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 6 }}>
              No hay facturas de compra cacheadas para {mes}. Sincronízalas desde Contabilidad.
            </div>
          )}
        </div>
      </ConfirmModal>
    </div>
  );
};

export default Rendiciones;
